import { useEffect } from "react";
import { useGlobalStore } from "./global";
import { getObtenerValorPorCodigo } from "../services/parameters";

export type FeatureFlagKey = "MOSTRAR_COMPLEMENTARIO";

export interface FeatureFlagDef {
  key: FeatureFlagKey;
  /** Human readable name shown in /devtools */
  label: string;
  /** Short explanation of what the flag controls */
  description: string;
  /** Build-time env var that provides the default (e.g. REACT_APP_FF_...) */
  envVar: string;
  /** Parametro code read from the backend (TBL_PARAMETROS.COD_PARAMETRO) */
  serverParamCode: string;
  /** Safe fallback when no other source resolves the flag */
  default: boolean;
}

/** Single source of truth for every feature flag in OFV. */
export const FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagDef> = {
  MOSTRAR_COMPLEMENTARIO: {
    key: "MOSTRAR_COMPLEMENTARIO",
    label: "Mostrar complementario",
    description:
      "Muestra las áreas de aportes voluntarios / cuenta complementaria en Mi Cuenta y Estado de Cuenta.",
    envVar: "REACT_APP_FF_MOSTRAR_COMPLEMENTARIO",
    serverParamCode: "MOSTRAR_COMPLEMENTARIO",
    default: false,
  },
};

export const FEATURE_FLAG_KEYS = Object.keys(
  FEATURE_FLAGS
) as FeatureFlagKey[];

const LOCALSTORAGE_PREFIX = "ff:";

/** Parse common boolean representations; returns undefined when not a known boolean. */
export function parseBool(value: unknown): boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  const s = String(value).trim().toLowerCase();
  if (s === "true" || s === "1") {
    return true;
  }
  if (s === "false" || s === "0") {
    return false;
  }
  return undefined;
}

export function getEnvDefault(def: FeatureFlagDef): boolean | undefined {
  return parseBool(process.env[def.envVar]);
}

export function getLocalOverride(key: FeatureFlagKey): boolean | undefined {
  try {
    return parseBool(localStorage.getItem(LOCALSTORAGE_PREFIX + key));
  } catch {
    return undefined;
  }
}

export function setLocalOverride(
  key: FeatureFlagKey,
  value: boolean | undefined
): void {
  try {
    if (value === undefined) {
      localStorage.removeItem(LOCALSTORAGE_PREFIX + key);
    } else {
      localStorage.setItem(LOCALSTORAGE_PREFIX + key, value ? "true" : "false");
    }
  } catch {
    /* localStorage unavailable (private mode, etc.) - ignore */
  }
}

export function clearLocalOverride(key: FeatureFlagKey): void {
  setLocalOverride(key, undefined);
}

/** Read all persisted overrides; used to seed the store on startup. */
export function loadAllLocalOverrides(): Partial<Record<FeatureFlagKey, boolean>> {
  const out: Partial<Record<FeatureFlagKey, boolean>> = {};
  FEATURE_FLAG_KEYS.forEach((k) => {
    const v = getLocalOverride(k);
    if (v !== undefined) {
      out[k] = v;
    }
  });
  return out;
}

export type FeatureFlagSource = "override" | "server" | "env" | "default";

export interface ResolvedFlag {
  value: boolean;
  source: FeatureFlagSource;
}

/**
 * Resolve a flag using the layered priority:
 * localStorage override > server Parametro > env default > hardcoded default.
 */
export function resolveFlag(
  key: FeatureFlagKey,
  opts: { serverValue?: boolean; override?: boolean }
): ResolvedFlag {
  const def = FEATURE_FLAGS[key];

  if (opts.override !== undefined) {
    return { value: opts.override, source: "override" };
  }
  if (opts.serverValue !== undefined) {
    return { value: opts.serverValue, source: "server" };
  }
  const env = getEnvDefault(def);
  if (env !== undefined) {
    return { value: env, source: "env" };
  }
  return { value: def.default, source: "default" };
}

/** Resolve a flag from the live store (value + source), reactive to changes. */
export function useResolvedFlag(key: FeatureFlagKey): ResolvedFlag {
  const serverValue = useGlobalStore((s) => s.featureFlagsServer[key]);
  const override = useGlobalStore((s) => s.featureFlagOverrides[key]);
  return resolveFlag(key, { serverValue, override });
}

/** Convenience hook returning just the resolved boolean for a flag. */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useResolvedFlag(key).value;
}

/**
 * Fetch every flag's backend Parametro once on app start and push the results
 * into the store. A missing/failed/inactive Parametro is ignored, leaving the
 * flag to fall back to its env/hardcoded default.
 */
export function useInitFeatureFlags(): void {
  const setServerFeatureFlags = useGlobalStore((s) => s.setServerFeatureFlags);

  useEffect(() => {
    let cancelled = false;

    FEATURE_FLAG_KEYS.forEach((key) => {
      const def = FEATURE_FLAGS[key];
      getObtenerValorPorCodigo(def.serverParamCode)
        .then((resp) => {
          const data = resp?.data;
          if (cancelled || !data?.successed || data.value?.estado !== true) {
            return;
          }
          const value = parseBool(data.value?.valor);
          if (value !== undefined) {
            setServerFeatureFlags({ [key]: value });
          }
        })
        .catch(() => {
          /* leave flag to fall back to env/default */
        });
    });

    return () => {
      cancelled = true;
    };
  }, [setServerFeatureFlags]);
}
